import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Bot, FileText, MessagesSquare,
  HelpCircle, Settings, ExternalLink, Power, CheckCircle2, XCircle,
  TrendingUp, UserPlus, Flame, LifeBuoy, ShieldCheck,
  Upload, Trash2, Plus, Download, RotateCw, FileUp,
} from "lucide-react";
import { adminApi } from "../api";

const TABS = [
  { id: "sources", label: "Fontes", icon: FileText },
  { id: "documents", label: "Documentos", icon: FileUp },
  { id: "sales", label: "Vendas (Insights)", icon: TrendingUp },
  { id: "support", label: "Suporte (Insights)", icon: LifeBuoy },
  { id: "leads", label: "Leads", icon: UserPlus },
  { id: "conversations", label: "Conversas", icon: MessagesSquare },
  { id: "unanswered", label: "Perguntas sem resposta", icon: HelpCircle },
  { id: "config", label: "Configuração", icon: Settings },
];

export default function AdminAI() {
  const [tab, setTab] = useState("sources");
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4" data-testid="ai-back-link">
        <ArrowLeft size={15} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-2 mb-1">
        <Bot size={22} className="text-[#0F1829]" />
        <h1 className="text-2xl font-bold text-[#0F1829]">Assistente IA</h1>
      </div>
      <p className="text-sm text-[#978D78] mb-5">Suporte/vendas público (widget WordPress) — base de conhecimento, conversas, perguntas sem resposta e configuração.</p>

      <div className="flex flex-wrap gap-1 border-b border-[#E6E1D6] mb-5" data-testid="ai-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ai-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t.id ? "border-[#0F1829] text-[#0F1829]" : "border-transparent text-[#978D78] hover:text-[#0F1829]"
              }`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "sources" && <SourcesTab />}
      {tab === "documents" && <DocumentsTab />}
      {tab === "sales" && <SalesTab />}
      {tab === "support" && <SupportTab />}
      {tab === "leads" && <LeadsTab />}
      {tab === "conversations" && <ConversationsTab />}
      {tab === "unanswered" && <UnansweredTab />}
      {tab === "config" && <ConfigTab />}
    </div>
  );
}

function Spinner() {
  return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#978D78]" /></div>;
}

// ─── Vendas (Insights) ──────────────────────────────────────────────────
function SalesTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    adminApi.aiSalesInsights(days).then((d) => setData(d)).catch(() => toast.error("Erro ao carregar insights")).finally(() => setLoading(false));
  }, [days]);
  if (loading) return <Spinner />;
  if (!data) return null;
  const ev = data.events || {};
  const Stat = ({ label, value, icon: Icon }) => (
    <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
      <div className="flex items-center gap-2 text-[#978D78] text-xs uppercase tracking-wider">{Icon && <Icon size={14} />} {label}</div>
      <div className="text-2xl font-bold text-[#0F1829] mt-1">{value ?? 0}</div>
    </div>
  );
  const List = ({ title, items }) => (
    <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
      <h3 className="text-sm font-semibold text-[#0F1829] mb-2">{title}</h3>
      {(!items || items.length === 0) ? <p className="text-xs text-[#B8B0A0]">Sem dados.</p> : (
        <ul className="space-y-1">{items.map((it, i) => (
          <li key={i} className="flex justify-between text-sm"><span className="text-[#5C5647]">{String(it.key).replace(/_/g, " ")}</span><span className="font-mono text-[#978D78]">{it.n}</span></li>
        ))}</ul>
      )}
    </div>
  );
  return (
    <div data-testid="ai-sales-insights">
      <div className="flex gap-1 mb-4">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`ai-sales-days-${d}`}
            className={`px-3 py-1 text-sm rounded ${days === d ? "bg-[#0F1829] text-white" : "bg-[#F5F2EB] text-[#5C5647]"}`}>{d} dias</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Sessões" value={ev.AI_SESSION_STARTED} icon={MessagesSquare} />
        <Stat label="Leads quentes" value={data.hot_leads} icon={Flame} />
        <Stat label="Contactos captados" value={ev.AI_CONTACT_CAPTURED} icon={UserPlus} />
        <Stat label="Handoffs" value={data.handoffs} icon={UserPlus} />
        <Stat label="Recomendações" value={ev.AI_RECOMMENDATION_SHOWN} icon={TrendingUp} />
        <Stat label="Comparações" value={ev.AI_COMPARISON_SHOWN} icon={TrendingUp} />
        <Stat label="Intenção de compra" value={ev.AI_PURCHASE_INTENT} icon={Flame} />
        <Stat label="Objeções" value={ev.AI_OBJECTION_DETECTED} icon={XCircle} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <List title="Top objetivos" items={data.top_goals} />
        <List title="Top objeções" items={data.top_objections} />
        <List title="Top recomendados" items={data.top_recommended} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <List title="Distribuição de temperatura" items={Object.entries(data.temperature_distribution || {}).map(([k, n]) => ({ key: k, n }))} />
        <List title="Intenção comercial" items={Object.entries(data.commercial_intent_distribution || {}).map(([k, n]) => ({ key: k, n }))} />
      </div>
    </div>
  );
}

// ─── Suporte (Insights) FASE 4 ──────────────────────────────────────────
function SupportTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    adminApi.aiSupportInsights(days).then((d) => setData(d)).catch(() => toast.error("Erro ao carregar insights de suporte")).finally(() => setLoading(false));
  }, [days]);
  if (loading) return <Spinner />;
  if (!data) return null;
  const ev = data.events || {};
  const pct = (v) => `${Math.round((v || 0) * 100)}%`;
  const Stat = ({ label, value, icon: Icon, tone }) => (
    <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
      <div className="flex items-center gap-2 text-[#978D78] text-xs uppercase tracking-wider">{Icon && <Icon size={14} />} {label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-[#0F1829]"}`}>{value ?? 0}</div>
    </div>
  );
  const List = ({ title, items }) => (
    <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
      <h3 className="text-sm font-semibold text-[#0F1829] mb-2">{title}</h3>
      {(!items || items.length === 0) ? <p className="text-xs text-[#B8B0A0]">Sem dados.</p> : (
        <ul className="space-y-1">{items.map((it, i) => (
          <li key={i} className="flex justify-between text-sm"><span className="text-[#5C5647]">{String(it.key).replace(/_/g, " ")}</span><span className="font-mono text-[#978D78]">{it.n}</span></li>
        ))}</ul>
      )}
    </div>
  );
  return (
    <div data-testid="ai-support-insights">
      <div className="flex gap-1 mb-4">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`ai-support-days-${d}`}
            className={`px-3 py-1 text-sm rounded ${days === d ? "bg-[#0F1829] text-white" : "bg-[#F5F2EB] text-[#5C5647]"}`}>{d} dias</button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Conversas de suporte" value={data.support_conversations} icon={LifeBuoy} />
        <Stat label="Auto-resolução" value={pct(data.self_service_resolution_rate)} icon={CheckCircle2} tone="text-[#0a7d3f]" />
        <Stat label="Taxa de escalação" value={pct(data.escalation_rate)} icon={UserPlus} tone="text-[#b45309]" />
        <Stat label="Sessões autenticadas" value={ev.AI_AUTHENTICATED_SESSION} icon={ShieldCheck} />
        <Stat label="Consultas de encomenda" value={ev.AI_ORDER_QUERY} icon={FileText} />
        <Stat label="Consultas de pagamento" value={ev.AI_PAYMENT_STATUS_QUERY} icon={FileText} />
        <Stat label="Consultas de formação" value={(ev.AI_UPCOMING_TRAINING_QUERY || 0) + (ev.AI_REGISTRATION_QUERY || 0)} icon={FileText} />
        <Stat label="Consultas de certificado" value={ev.AI_CERTIFICATE_QUERY} icon={FileText} />
        <Stat label="Chamadas a ferramentas privadas" value={data.private_tool_calls} icon={ShieldCheck} />
        <Stat label="Acessos negados (segurança)" value={data.access_denied_attempts} icon={XCircle} tone={(data.access_denied_attempts > 0) ? "text-[#b91c1c]" : "text-[#0F1829]"} />
        <Stat label="Logins pedidos" value={ev.AI_LOGIN_REQUIRED} icon={Power} />
        <Stat label="Escalações" value={ev.AI_SUPPORT_ESCALATED} icon={UserPlus} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <List title="Top intents de suporte" items={data.top_support_intents} />
        <List title="Distribuição de resolução" items={Object.entries(data.resolution_distribution || {}).map(([k, n]) => ({ key: k, n }))} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <List title="Lacunas de conhecimento (mais escalam / não resolvem)" items={data.knowledge_gaps} />
        <div className="bg-white border border-[#F3F0E7] rounded-lg p-4">
          <h3 className="text-sm font-semibold text-[#0F1829] mb-2">Feedback do cliente</h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><div className="text-xl font-bold text-[#0a7d3f]">{data.feedback_confirmed || 0}</div><div className="text-xs text-[#978D78]">Resolvido</div></div>
            <div><div className="text-xl font-bold text-[#b91c1c]">{data.feedback_not_resolved || 0}</div><div className="text-xs text-[#978D78]">Não resolvido</div></div>
            <div><div className="text-xl font-bold text-[#0F1829]">{data.open_cases || 0}</div><div className="text-xs text-[#978D78]">Casos abertos</div></div>
          </div>
        </div>
      </div>
      <SupportCases />
    </div>
  );
}

function SupportCases() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    adminApi.aiSupportCases().then((d) => setRows(d.cases || [])).catch(() => setRows([]));
  }, []);
  if (!rows) return null;
  return (
    <div className="bg-white border border-[#F3F0E7] rounded-lg p-4 mt-3" data-testid="ai-support-cases">
      <h3 className="text-sm font-semibold text-[#0F1829] mb-2">Casos de suporte (base Fase 5)</h3>
      {rows.length === 0 ? <p className="text-xs text-[#B8B0A0]">Sem casos.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#978D78] text-xs uppercase">
              <th className="py-1 pr-3">Cliente</th><th className="py-1 pr-3">Assunto</th>
              <th className="py-1 pr-3">Prioridade</th><th className="py-1 pr-3">Estado</th>
              <th className="py-1 pr-3">Consultor</th><th className="py-1 pr-3">Feedback</th>
            </tr></thead>
            <tbody>{rows.map((c) => (
              <tr key={c.id} className="border-t border-[#F3F0E7]" data-testid="ai-support-case-row">
                <td className="py-1 pr-3 text-[#5C5647]">{c.customer_name || `#${c.id_cliente || "-"}`}</td>
                <td className="py-1 pr-3 text-[#5C5647]">{String(c.intent || "").replace(/_/g, " ")}</td>
                <td className="py-1 pr-3"><span className={`px-2 py-0.5 rounded text-xs ${c.priority === "HIGH" ? "bg-[#fde8e8] text-[#b91c1c]" : c.priority === "MEDIUM" ? "bg-[#fef3e2] text-[#b45309]" : "bg-[#F5F2EB] text-[#5C5647]"}`}>{c.priority}</span></td>
                <td className="py-1 pr-3 text-[#5C5647]">{c.status}</td>
                <td className="py-1 pr-3 text-[#5C5647]">{c.consultant?.nome || "—"}</td>
                <td className="py-1 pr-3 text-[#5C5647]">{c.feedback ? (c.feedback === "RESOLVED_CONFIRMED" ? "✓" : "✗") : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Leads / Handoffs ───────────────────────────────────────────────────
function LeadsTab() {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    setRows(null);
    adminApi.aiLeads({ status }).then((d) => setRows(d.leads || [])).catch(() => toast.error("Erro ao carregar leads"));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const setLeadStatus = async (id, st) => {
    try { await adminApi.aiLeadStatus(id, st); toast.success("Estado atualizado"); load(); } catch { toast.error("Erro"); }
  };
  const tempColor = (t) => ({ VERY_HOT: "bg-red-100 text-red-700", HOT: "bg-orange-100 text-orange-700", WARM: "bg-amber-100 text-amber-700", COLD: "bg-slate-100 text-slate-600" }[t] || "bg-slate-100 text-slate-600");
  return (
    <div data-testid="ai-leads">
      <div className="flex gap-1 mb-4">
        {[["", "Todos"], ["NEW", "Novos"], ["PENDING", "Pendentes"], ["RESOLVED", "Resolvidos"]].map(([s, l]) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1 text-sm rounded ${status === s ? "bg-[#0F1829] text-white" : "bg-[#F5F2EB] text-[#5C5647]"}`}>{l}</button>
        ))}
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? <p className="text-sm text-[#978D78] py-6 text-center">Sem leads neste filtro.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ai-leads-table">
            <thead><tr className="text-left text-[#978D78] border-b border-[#F3F0E7]">
              <th className="py-2 pr-3 font-semibold">Data</th><th className="py-2 pr-3 font-semibold">Contacto</th>
              <th className="py-2 pr-3 font-semibold">Objetivo</th><th className="py-2 pr-3 font-semibold">Recomendado</th>
              <th className="py-2 pr-3 font-semibold">Score</th><th className="py-2 pr-3 font-semibold">Temp.</th>
              <th className="py-2 pr-3 font-semibold">Objeções</th><th className="py-2 font-semibold">Estado</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F8F6F0] align-top" data-testid={`ai-lead-${r.id}`}>
                  <td className="py-2 pr-3 text-xs text-[#978D78] whitespace-nowrap">{r.requested_at ? r.requested_at.slice(0, 16).replace("T", " ") : "-"}</td>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-[#1B1A17]">{r.name || "—"}</div>
                    {r.email && <div className="text-xs text-blue-700">{r.email}</div>}
                    {r.phone && <div className="text-xs text-[#5C5647]">{r.phone}</div>}
                  </td>
                  <td className="py-2 pr-3 text-[#5C5647]">{(r.primary_goal || "").replace(/_/g, " ") || "—"}</td>
                  <td className="py-2 pr-3 text-[#5C5647]">{(r.recommended || []).join(", ") || "—"}</td>
                  <td className="py-2 pr-3 font-mono">{r.lead_score ?? 0}</td>
                  <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${tempColor(r.lead_temperature)}`}>{r.lead_temperature || "COLD"}</span></td>
                  <td className="py-2 pr-3 text-xs text-[#B08900]">{(r.objections || []).join(", ")}</td>
                  <td className="py-2">
                    <select value={r.status} onChange={(e) => setLeadStatus(r.id, e.target.value)} data-testid={`ai-lead-status-${r.id}`}
                      className="text-xs border border-[#E6E1D6] rounded px-1.5 py-1">
                      {["NEW", "PENDING", "RESOLVED", "CRM_SYNC_PENDING"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Fontes / KB ────────────────────────────────────────────────────────
function SourcesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [addingUrl, setAddingUrl] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminApi.aiSources({ limit: 300 })); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const reindex = async (full) => {
    setReindexing(true);
    try {
      const r = await adminApi.aiReindex(full);
      const s = r.summary || {};
      if (s.skipped) toast.info("Indexação desativada na configuração.");
      else toast.success(`Indexação: ${s.sources_seen || 0} fontes · ${s.changed || 0} alteradas · ${s.chunks || 0} chunks`);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setReindexing(false); }
  };

  const addUrl = async () => {
    const url = newUrl.trim();
    if (!url) return;
    setAddingUrl(true);
    try {
      const r = await adminApi.aiAddUrl(url);
      const res = r.result || {};
      toast.success(`URL indexado: ${res.title || url} · ${res.chunks || 0} chunks`);
      setNewUrl("");
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setAddingUrl(false); }
  };

  const refresh = async (s) => {
    setRefreshingId(s.id);
    try {
      const r = await adminApi.aiRefreshSource({ id: s.id });
      const res = r.result || {};
      toast.success(res.changed ? `Atualizado · ${res.chunks || 0} chunks` : "Já estava atualizado.");
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setRefreshingId(null); }
  };

  const deactivate = async (id) => {
    try { await adminApi.aiDeactivateSource(id); toast.success("Fonte desativada."); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const isDoc = (s) => (s.url || "").startsWith("doc://") || s.content_type === "document";

  return (
    <div data-testid="ai-sources-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="text-sm text-[#5C5647]">
          {data ? <>{data.total} fontes · <b>{data.active_chunks}</b> chunks ativos</> : "—"}
        </div>
        <div className="flex gap-2">
          <button onClick={() => reindex(false)} disabled={reindexing} data-testid="ai-reindex-btn"
            className="inline-flex items-center gap-1.5 bg-[#0F1829] text-white text-sm px-3 py-2 rounded-lg disabled:opacity-50">
            {reindexing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Reindexar
          </button>
          <button onClick={() => reindex(true)} disabled={reindexing} data-testid="ai-reindex-full-btn"
            className="inline-flex items-center gap-1.5 border border-[#0F1829] text-[#0F1829] text-sm px-3 py-2 rounded-lg disabled:opacity-50">
            Reindex. completo
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 bg-[#FBFAF6] border border-[#F3F0E7] rounded-lg p-2">
        <input
          value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addUrl(); }}
          placeholder="https://ihavethepower.net/pagina-a-indexar/"
          data-testid="ai-add-url-input"
          className="flex-1 bg-white border border-[#E6E1D6] rounded-md px-3 py-2 text-sm outline-none focus:border-[#0F1829]"
        />
        <button onClick={addUrl} disabled={addingUrl || !newUrl.trim()} data-testid="ai-add-url-btn"
          className="inline-flex items-center gap-1.5 bg-[#0F1829] text-white text-sm px-3 py-2 rounded-lg disabled:opacity-50 whitespace-nowrap">
          {addingUrl ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Adicionar URL
        </button>
      </div>

      {loading ? <Spinner /> : (
        (data?.sources || []).length === 0 ? (
          <p className="text-sm text-[#978D78] py-8 text-center" data-testid="ai-sources-empty">
            Sem fontes indexadas. Corre uma reindexação ou adiciona um URL acima.
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#F3F0E7] rounded-lg">
            <table className="w-full text-sm" data-testid="ai-sources-table">
              <thead><tr className="text-left text-[#978D78] border-b border-[#F3F0E7] bg-[#FBFAF6]">
                <th className="py-2 px-3 font-semibold">Título</th>
                <th className="py-2 px-3 font-semibold">Tipo</th>
                <th className="py-2 px-3 font-semibold text-center">Chunks</th>
                <th className="py-2 px-3 font-semibold text-center">Estado</th>
                <th className="py-2 px-3 font-semibold">Últ. indexação</th>
                <th className="py-2 px-3 font-semibold"></th>
              </tr></thead>
              <tbody>
                {data.sources.map((s) => (
                  <tr key={s.id} className="border-b border-[#F8F6F0] hover:bg-[#FBFAF6]" data-testid={`ai-source-${s.id}`}>
                    <td className="py-2 px-3">
                      {isDoc(s) ? (
                        <span className="text-[#0F1829] inline-flex items-center gap-1">
                          <FileUp size={11} className="opacity-50" /> {s.title || s.url}
                        </span>
                      ) : (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#0F1829] hover:underline inline-flex items-center gap-1">
                          {s.title || s.url} <ExternalLink size={11} className="opacity-50" />
                        </a>
                      )}
                    </td>
                    <td className="py-2 px-3 text-[#978D78]">{s.content_type}</td>
                    <td className="py-2 px-3 text-center tabular-nums">{s.n_chunks}</td>
                    <td className="py-2 px-3 text-center">
                      {s.active
                        ? <span className="inline-flex items-center gap-1 text-green-700 text-xs"><CheckCircle2 size={13} /> Ativa</span>
                        : <span className="inline-flex items-center gap-1 text-[#B8B0A0] text-xs"><XCircle size={13} /> Inativa</span>}
                    </td>
                    <td className="py-2 px-3 text-[#978D78] text-xs">{s.last_indexed ? new Date(s.last_indexed).toLocaleString("pt-PT") : "—"}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {s.active && !isDoc(s) && (
                        <button onClick={() => refresh(s)} disabled={refreshingId === s.id} data-testid={`ai-source-refresh-${s.id}`}
                          className="text-xs text-[#0F1829] hover:underline inline-flex items-center gap-1 mr-3 disabled:opacity-50">
                          {refreshingId === s.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />} Atualizar
                        </button>
                      )}
                      {s.active && (
                        <button onClick={() => deactivate(s.id)} data-testid={`ai-source-deactivate-${s.id}`}
                          className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                          <Power size={12} /> Desativar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ─── Documentos (PDF/Word/PPTX/TXT) ─────────────────────────────────────
function DocumentsTab() {
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.aiDocuments(); setDocs(r.documents || []); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const r = await adminApi.aiUploadDocument(f);
        const res = r.result || {};
        toast.success(`${res.filename || f.name} · ${res.chunks || 0} chunks`);
      }
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (doc_id) => {
    try { await adminApi.aiDeleteDocument(doc_id); toast.success("Documento removido."); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

  return (
    <div data-testid="ai-documents-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-[#5C5647] max-w-xl">
          Carrega documentos (PDF, Word, PowerPoint, TXT/Markdown). O texto é extraído e adicionado à
          base de conhecimento do assistente; o ficheiro original fica guardado na CDN.
        </p>
        <label data-testid="ai-doc-upload-label"
          className={`inline-flex items-center gap-1.5 bg-[#0F1829] text-white text-sm px-3 py-2 rounded-lg cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Carregar documento
          <input type="file" multiple accept=".pdf,.docx,.txt,.md,.pptx" className="hidden"
            onChange={onUpload} data-testid="ai-doc-upload-input" />
        </label>
      </div>

      {loading ? <Spinner /> : (
        (docs || []).length === 0 ? (
          <p className="text-sm text-[#978D78] py-8 text-center" data-testid="ai-documents-empty">
            Ainda não carregaste documentos. Usa o botão acima para adicionar PDFs, Word, PowerPoint ou TXT.
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#F3F0E7] rounded-lg">
            <table className="w-full text-sm" data-testid="ai-documents-table">
              <thead><tr className="text-left text-[#978D78] border-b border-[#F3F0E7] bg-[#FBFAF6]">
                <th className="py-2 px-3 font-semibold">Ficheiro</th>
                <th className="py-2 px-3 font-semibold">Tipo</th>
                <th className="py-2 px-3 font-semibold text-center">Chunks</th>
                <th className="py-2 px-3 font-semibold text-center">Tamanho</th>
                <th className="py-2 px-3 font-semibold">Carregado</th>
                <th className="py-2 px-3 font-semibold"></th>
              </tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.doc_id} className="border-b border-[#F8F6F0] hover:bg-[#FBFAF6]" data-testid={`ai-doc-${d.doc_id}`}>
                    <td className="py-2 px-3 text-[#0F1829]">{d.filename}</td>
                    <td className="py-2 px-3 text-[#978D78] uppercase text-xs">{d.ext}</td>
                    <td className="py-2 px-3 text-center tabular-nums">{d.n_chunks}</td>
                    <td className="py-2 px-3 text-center text-[#978D78] text-xs">{fmtSize(d.size)}</td>
                    <td className="py-2 px-3 text-[#978D78] text-xs">{d.created_at ? new Date(d.created_at).toLocaleString("pt-PT") : "—"}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <a href={adminApi.aiDocumentDownloadUrl(d.doc_id)} target="_blank" rel="noopener noreferrer"
                        data-testid={`ai-doc-download-${d.doc_id}`}
                        className="text-xs text-[#0F1829] hover:underline inline-flex items-center gap-1 mr-3">
                        <Download size={12} /> Descarregar
                      </a>
                      <button onClick={() => remove(d.doc_id)} data-testid={`ai-doc-delete-${d.doc_id}`}
                        className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                        <Trash2 size={12} /> Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ─── Conversas ──────────────────────────────────────────────────────────
function ConversationsTab() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await adminApi.aiConversations(80); setRows(r.conversations || []); }
      catch (e) { toast.error(e.message); setRows([]); }
    })();
  }, []);

  const open = async (sid) => {
    setLoadingDetail(true); setDetail({ session_id: sid, messages: [] });
    try { const r = await adminApi.aiConversationDetail(sid); setDetail({ session_id: sid, messages: r.messages || [] }); }
    catch (e) { toast.error(e.message); }
    finally { setLoadingDetail(false); }
  };

  if (rows === null) return <Spinner />;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="ai-conversations-tab">
      <div className="border border-[#F3F0E7] rounded-lg overflow-hidden">
        {rows.length === 0 ? <p className="text-sm text-[#978D78] p-6 text-center">Sem conversas ainda.</p> : (
          <div className="divide-y divide-[#F8F6F0] max-h-[70vh] overflow-y-auto">
            {rows.map((c) => (
              <button key={c.session_id} onClick={() => open(c.session_id)} data-testid={`ai-conv-${c.session_id}`}
                className={`w-full text-left px-3 py-2.5 hover:bg-[#FBFAF6] ${detail?.session_id === c.session_id ? "bg-[#F3F0E7]" : ""}`}>
                <div className="flex justify-between gap-2">
                  <span className="text-sm font-medium text-[#1B1A17] truncate">{(c.intents || []).slice(0, 2).join(", ") || "—"}</span>
                  <span className="text-[11px] text-[#978D78] whitespace-nowrap">{c.n_messages} msg</span>
                </div>
                <div className="text-[11px] text-[#978D78] truncate">{c.page || c.visitor_id || c.session_id}</div>
                <div className="text-[10px] text-[#B8B0A0]">{c.updated_at ? new Date(c.updated_at).toLocaleString("pt-PT") : ""}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="border border-[#F3F0E7] rounded-lg p-3 max-h-[70vh] overflow-y-auto bg-[#FBFAF6]">
        {!detail ? <p className="text-sm text-[#978D78] p-6 text-center">Seleciona uma conversa.</p> :
          loadingDetail ? <Spinner /> : (
            <div className="space-y-2" data-testid="ai-conv-detail">
              {detail.messages.map((m, i) => (
                <div key={i} className={`p-2.5 rounded-lg text-sm ${m.role === "user" ? "bg-[#0F1829] text-white ml-8" : "bg-white border border-[#E6E1D6] mr-8"}`}>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                  {m.role === "assistant" && (m.sources || []).length > 0 && (
                    <div className="text-[10px] mt-1 opacity-70">Fontes: {m.sources.map((s) => s.title).filter(Boolean).join(" · ")}</div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Perguntas sem resposta ───────────────────────────────────────────────
function UnansweredTab() {
  const [status, setStatus] = useState("OPEN");
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    setRows(null);
    try { const r = await adminApi.aiUnanswered({ status, limit: 200 }); setRows(r.items || []); }
    catch (e) { toast.error(e.message); setRows([]); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const setItemStatus = async (id, s) => {
    try { await adminApi.aiUnansweredStatus(id, s); toast.success("Atualizado."); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="ai-unanswered-tab">
      <div className="flex gap-1 mb-4">
        {["OPEN", "RESOLVED", "IGNORED"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`ai-unanswered-filter-${s}`}
            className={`px-3 py-1.5 text-sm rounded-lg ${status === s ? "bg-[#0F1829] text-white" : "border border-[#E6E1D6] text-[#5C5647]"}`}>
            {s === "OPEN" ? "Abertas" : s === "RESOLVED" ? "Resolvidas" : "Ignoradas"}
          </button>
        ))}
      </div>
      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <p className="text-sm text-[#978D78] py-8 text-center" data-testid="ai-unanswered-empty">Nada aqui.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="border border-[#F3F0E7] rounded-lg p-3" data-testid={`ai-unanswered-${r.id}`}>
              <div className="flex justify-between gap-3 flex-wrap">
                <p className="text-sm text-[#1B1A17] font-medium flex-1">{r.question}</p>
                <span className="text-xs bg-[#F3F0E7] text-[#5C5647] px-2 py-0.5 rounded whitespace-nowrap">{r.frequency}× · {(r.intent || []).join(", ")}</span>
              </div>
              <div className="text-[11px] text-[#978D78] mt-1">{r.page_url || "—"} · últ. {r.last_seen ? new Date(r.last_seen).toLocaleString("pt-PT") : ""}</div>
              {status === "OPEN" && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setItemStatus(r.id, "RESOLVED")} className="text-xs text-green-700 hover:underline">Marcar resolvida</button>
                  <button onClick={() => setItemStatus(r.id, "IGNORED")} className="text-xs text-[#978D78] hover:underline">Ignorar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Configuração ──────────────────────────────────────────────────────
function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await adminApi.aiGetConfig(); setCfg(r.config); }
      catch (e) { toast.error(e.message); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        enabled: cfg.enabled, display_name: cfg.display_name, system_prompt: cfg.system_prompt,
        welcome_message: cfg.welcome_message, welcome_message_course: cfg.welcome_message_course,
        model_provider: cfg.model_provider, model_name: cfg.model_name,
        wordpress_base_url: cfg.wordpress_base_url, min_retrieval_score: Number(cfg.min_retrieval_score),
        max_retrieved_chunks: Number(cfg.max_retrieved_chunks), history_window: Number(cfg.history_window),
        rate_limit_per_min: Number(cfg.rate_limit_per_min), rate_limit_burst: Number(cfg.rate_limit_burst),
        knowledge_indexing_enabled: cfg.knowledge_indexing_enabled, proactive_enabled: cfg.proactive_enabled,
      };
      const r = await adminApi.aiSetConfig(patch);
      setCfg(r.config); toast.success("Configuração guardada.");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (!cfg) return <Spinner />;
  const F = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const input = "w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded-lg outline-none focus:border-[#0F1829]";
  const label = "block text-xs uppercase tracking-wider text-[#978D78] mb-1 mt-3";

  return (
    <div className="max-w-2xl" data-testid="ai-config-tab">
      <label className="flex items-center gap-2 mb-3">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => F("enabled", e.target.checked)} data-testid="ai-cfg-enabled" />
        <span className="text-sm font-medium text-[#1B1A17]">Assistente ativo</span>
      </label>
      <label className="flex items-center gap-2 mb-3">
        <input type="checkbox" checked={!!cfg.knowledge_indexing_enabled} onChange={(e) => F("knowledge_indexing_enabled", e.target.checked)} data-testid="ai-cfg-indexing" />
        <span className="text-sm text-[#1B1A17]">Indexação de conhecimento ativa</span>
      </label>

      <label className={label}>Nome apresentado</label>
      <input className={input} value={cfg.display_name || ""} onChange={(e) => F("display_name", e.target.value)} data-testid="ai-cfg-name" />

      <label className={label}>Mensagem de boas-vindas (genérica)</label>
      <textarea className={input} rows={2} value={cfg.welcome_message || ""} onChange={(e) => F("welcome_message", e.target.value)} data-testid="ai-cfg-welcome" />

      <label className={label}>Mensagem de boas-vindas (página de curso/produto — usa {"{title}"} se quiseres)</label>
      <textarea className={input} rows={2} value={cfg.welcome_message_course || ""} onChange={(e) => F("welcome_message_course", e.target.value)} data-testid="ai-cfg-welcome-course" />

      <label className={label}>System prompt</label>
      <textarea className={`${input} font-mono text-xs`} rows={8} value={cfg.system_prompt || ""} onChange={(e) => F("system_prompt", e.target.value)} data-testid="ai-cfg-prompt" />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Provider</label>
          <input className={input} value={cfg.model_provider || ""} onChange={(e) => F("model_provider", e.target.value)} data-testid="ai-cfg-provider" />
        </div>
        <div>
          <label className={label}>Modelo</label>
          <input className={input} value={cfg.model_name || ""} onChange={(e) => F("model_name", e.target.value)} data-testid="ai-cfg-model" />
        </div>
        <div>
          <label className={label}>WordPress base URL</label>
          <input className={input} value={cfg.wordpress_base_url || ""} onChange={(e) => F("wordpress_base_url", e.target.value)} data-testid="ai-cfg-wp-url" />
        </div>
        <div>
          <label className={label}>Min. score retrieval</label>
          <input className={input} type="number" step="0.1" value={cfg.min_retrieval_score} onChange={(e) => F("min_retrieval_score", e.target.value)} data-testid="ai-cfg-minscore" />
        </div>
        <div>
          <label className={label}>Máx. chunks</label>
          <input className={input} type="number" value={cfg.max_retrieved_chunks} onChange={(e) => F("max_retrieved_chunks", e.target.value)} />
        </div>
        <div>
          <label className={label}>Janela de histórico</label>
          <input className={input} type="number" value={cfg.history_window} onChange={(e) => F("history_window", e.target.value)} />
        </div>
        <div>
          <label className={label}>Rate limit / min</label>
          <input className={input} type="number" value={cfg.rate_limit_per_min} onChange={(e) => F("rate_limit_per_min", e.target.value)} />
        </div>
        <div>
          <label className={label}>Burst (5s)</label>
          <input className={input} type="number" value={cfg.rate_limit_burst} onChange={(e) => F("rate_limit_burst", e.target.value)} />
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="ai-cfg-save"
        className="mt-5 inline-flex items-center gap-1.5 bg-[#0F1829] text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Guardar configuração
      </button>
    </div>
  );
}
